<script setup lang="ts">
import { iconColors } from 'assets/js/utils';
import { LockKeyhole, Sparkles } from 'lucide-vue-next';
import type { Attachment, Paragraph, Project, StructuredChapter } from '~/api/types';
import ImageLayout from '~/components/ImageLayout.vue';
import LoadingDots from '~/components/LoadingDots.vue';
// Lazy load the editor component
import { defineAsyncComponent } from 'vue';
import { IMAGE_ATTACHMENT_TYPES, MERMAID_ATTACHMENT_TYPES } from '~/constants/attachments';
const ContentEditor = defineAsyncComponent(() => import('./editor/ContentEditor.vue'));

type Props = {
  paragraph: Paragraph;
  project: Project;
  chapter: StructuredChapter;
};

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:project': [project: Project];
}>();

const user = useUserStore();

const isClient = ref(false);
onMounted(() => {
  isClient.value = true;
});

const isEditorReady = ref(false);
const view = computed(() => {
  const hasPermissions = props.project.is_owner && props.project.user_id === user.user?.id && props.project.payed_status;
  const isContentReturned = props.paragraph.content && props.paragraph.content.length > 600;
  if (hasPermissions && isContentReturned && isClient.value) return 'edit';
  if (!hasPermissions && !isContentReturned) return 'blurred';
  if (hasPermissions || isContentReturned) return 'text';
  return 'blurred';
});
</script>

<template>
  <div class="content-page">
    <template v-if="view != 'edit' || !isEditorReady">
      <h2
        v-if="chapter.paragraphs.indexOf(paragraph) === 0 && !chapter.is_paragraph"
        :id="chapter.id.toString()"
        class="content-page__heading"
      >
        {{ chapter.title }}
      </h2>
      <h2
        :id="paragraph.id.toString()"
        class="content-page__heading"
      >
        {{ paragraph.title }}
      </h2>
    </template>

    <template v-if="view != 'edit' || !isEditorReady">
      <template v-if="paragraph.status && paragraph.attachments?.length">
        <template v-if="paragraph.attachments?.length">
          <image-layout
            v-for="attachment in paragraph.attachments?.filter((a: Attachment) => MERMAID_ATTACHMENT_TYPES.includes(a.type))"
            :key="attachment.id"
            type="mermaid"
            :code="attachment.code"
            :text="attachment.caption"
            itemprop="image"
            class="w-full"
          />
          <image-layout
            v-for="attachment in paragraph.attachments?.filter((a: Attachment) => IMAGE_ATTACHMENT_TYPES.includes(a.type) && a.picture_url)"
            :key="attachment.id"
            type="image"
            :src="attachment.picture_url"
            :alt="attachment.caption || ''"
            :text="attachment.caption"
            itemprop="image"
            class="w-full"
          />
        </template>
        <template v-else>
          <image-layout
            v-if="paragraph.pictures && paragraph.pictures.matched_images?.length"
            type="image"
            :src="paragraph.pictures?.matched_images[0]"
            :alt="paragraph.pictures.caption || ''"
            itemprop="image"
            :text="paragraph.pictures.caption"
            class="w-full"
          />
        </template>
      </template>
    </template>
    <div
      v-if="view === 'edit'"
      class="project-page__chapter-content"
    >
      <client-only :fallback="paragraph.content">
        <div
          v-if="!isEditorReady"
          class="project-page__chapter-content"
        >
          {{ paragraph.content }}
        </div>
        <content-editor
          v-show="isEditorReady"
          :paragraph="paragraph"
          :project="project"
          @update:project="emit('update:project', $event)"
          @ready="isEditorReady = true"
        />
      </client-only>
    </div>
    <div
      v-else-if="view === 'text'"
      class="project-page__chapter-content"
    >
      {{ paragraph.content }}
    </div>
    <div
      v-else
      class="blurred-content"
    >
      <div
        class="blurred-content__text"
        :class="[paragraph.title.toLowerCase().includes('список литературы') ? 'source' : 'text']"
      />
      <div
        class="blurred-content__text"
        :class="[paragraph.title.toLowerCase().includes('список литературы') ? '' : 'text']"
      />
      <div class="blurred-content-gradient"></div>
      <div class="blurred-content__card">
        <div class="blurred-content__card__content">
          <span class="blurred-content__card__title">Текст доступен в расширенной версии</span>
          <p class="blurred-content__card__text">
            {{ paragraph.content }}
          </p>
          <div class="blurred-content__card__lock">
            <LockKeyhole style="width: 28px; height: 28px" />
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="!paragraph.status"
      class="project-page__chapter-loader"
    >
      <Sparkles
        :size="20"
        :color="iconColors.primary"
      />
      Генерируем текст
      <loading-dots />
    </div>
  </div>
</template>

<style scoped lang="scss">
.content-page {
  padding: 64px;
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: start;
  gap: 40px;
  min-height: 800px;

  @include media-breakpoint-down(md) {
    padding: 48px;
    min-height: 640px;
  }

  .project-page__chapter-loader {
    font-family: 'Manrope', sans-serif;
    margin-top: 8px;
    border-radius: 12px;
    border: 1px solid $line-stroke;
    background: $background-theme-fade;
    padding: 10px 16px;

    color: $foreground-theme;
    font-size: 16px;
    font-weight: 600;
    line-height: 28px;
    display: flex;
    gap: 8px;
    align-items: center;
  }

  .mermaid {
    padding: 16px;
    border-radius: 12px;
    border: 1px solid $background-button-secondary-hover;
    width: 100%;
  }

  .content-page__heading {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    line-height: 26px;
    font-family: 'Times New Roman', serif;
    scroll-margin-top: 32px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;

    .edit-button {
      font-family: 'Manrope', sans-serif;
      font-size: 14px;
    }
  }

  .project-page__chapter-content {
    white-space: pre-wrap;
    // overflow: hidden;
    font-size: 16px;
    font-weight: 400;
    line-height: 26px;
    text-align: justify;
    text-wrap: wrap;
    word-break: break-word;
    width: 100%;

    @include media-breakpoint-down(md) {
      text-align: start;
    }
  }

  .blurred-content {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 24px;

    .blurred-content-gradient {
      position: absolute;
      left: -10px;
      bottom: -10px;
      width: 105%;
      height: 105%;
      background: rgb(255, 255, 255);
      background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
    }

    .blurred-content__text {
      //color: transparent;
      //text-shadow: 0 0 14px #444444;
      user-select: none;
      filter: blur(4px);
      opacity: 75%;

      &.text {
        &:first-child {
          &::after {
            content: 'В современном мире, где быстрое развитие технологий и инноваций становится нормой, реализация качественного проекта играет ключевую роль в достижении поставленных целей. Данный проект представляет собой значимый шаг в направлении решения актуальных проблем в выбранной области, будь то наука, экономика, социальные исследования или любая другая сфера. Одной из главных ценностей данного проекта является его способность внести значимый вклад в развитие теоретических и практических знаний. С помощью глубокого анализа и тщательного изучения рассматриваемых вопросов мы стремимся выявить новые подходы и методы, которые могут быть применены для повышения эффективности процессов или улучшения качества результатов в соответствующей области. Кроме того, проект обладает потенциалом для практического применения, что делает его актуальным не только в научной среде, но и в реальной жизни. Создание и внедрение инновационных решений может значительно повысить конкурентоспособность организаций, улучшить качество услуг и продуктов, а также оптимизировать затраты. Таким образом, результаты нашего исследования могут быть использованы как в академических, так и в профессиональных кругах, способствуя обмену знаниями и практиками. Важность данного проекта также отмечается в его способности способствовать социальному развитию.';

            @include media-breakpoint-down(md) {
              content: 'Реализация качественного проекта играет ключевую роль в достижении поставленных целей. Данный проект представляет собой значимый шаг в направлении решения актуальных проблем в выбранной области, будь то наука, экономика, социальные исследования или любая другая сфера. Одной из главных ценностей данного проекта является его способность внести значимый вклад в развитие теоретических и практических знаний.';
            }
          }
        }

        &:not(:first-child) {
          &::after {
            content: 'Важность данного проекта также отмечается в его способности способствовать социальному развитию. Ориентируясь на актуальные потребности и проблемы общества, мы можем предложить действенные решения, направленные на улучшение качества жизни людей, защиту окружающей среды и устойчивое развитие. Наконец, данный проект имеет значительный потенциал для дальнейших исследований и разработок. Полученные в процессе работы результаты открывают новые горизонты для глубокого изучения и анализа, способствуя формированию новых гипотез и направлений для будущих научных работ. Проект обладает потенциалом для практического применения, что делает его актуальным не только в научной среде, но и в реальной жизни. Создание и внедрение инновационных решений может значительно повысить конкурентоспособность организаций, улучшить качество услуг и продуктов, а также оптимизировать затраты. Таким образом, результаты нашего исследования могут быть использованы как в академических, так и в профессиональных кругах, способствуя обмену знаниями и практиками.';

            @include media-breakpoint-down(md) {
              content: 'Реализация качественного проекта играет ключевую роль в достижении поставленных целей. Данный проект представляет собой значимый шаг в направлении решения актуальных проблем в выбранной области, будь то наука, экономика, социальные исследования или любая другая сфера.';
            }
          }
        }
      }

      &.source {
        &::after {
          white-space: pre-line;
          word-break: break-word;
          hyphens: auto;
          content: '1. Проблема квалифицированных кадров в строительстве | Статья... [Электронный ресурс] // moluch.ru - Режим доступа: https://moluch.ru/archive/79/13889/, свободный. - Загл. с экрана \A \
          2. Эксперты обсудили проблему квалификационных компетенций... [Электронный ресурс] // asonsk.ru - Режим доступа: https://asonsk.ru/information/eksperty-obsudili-problemu-otstavaniya-i-kvalifikatsionnykh-kompetentsiy-v-sfere-stroitelstva/, свободный. - Загл. с экрана \A \
          3. Дородных А. А. Проблемы подготовки специалистов в области строительства // Интеллектуальный потенциал XXI века: ступени познания. 2010. №2. URL: https://cyberleninka.ru/article/n/problemy-podgotovki-spetsialistov-v-oblasti-stroitelstva (дата обращения: 26.11.2024). \A \
          4. Report [Электронный ресурс] // nostroy-sdo.ru - Режим доступа: https://nostroy-sdo.ru/staticcontent/презентация нок нострой.pdf, свободный. - Загл. с экрана \A \
          5. Повышение квалификации строителей: проблемы и пути... [Электронный ресурс] // spravochnick.ru - Режим доступа: https://spravochnick.ru/arhitektura_i_stroitelstvo/povyshenie_kvalifikacii_stroiteley_problemy_i_puti_ih_resheniya/, свободный. - Загл. с экрана \A \
        6. Курсы повышения квалификации [Электронный ресурс] // arpo.su - Режим доступа: https://arpo.su/kursy-povyshenija-kvalifikacii/, свободный. - Загл. с экрана \A \
        7. Повышение квалификации по программе "Организация..." [Электронный ресурс] // novosibirsk.aprofpk.ru - Режим доступа: https://novosibirsk.aprofpk.ru/services/kursy-povysheniya-kvalifikatsii/stroitelstvo/organizatsiya-stroitelnoj-deyatelnosti/, свободный. - Загл. с экрана \A \
        8. Программы повышения квалификации в области строительства... [Электронный ресурс] // novosibirsk.ipk-ipp.ru - Режим доступа: https://novosibirsk.ipk-ipp.ru/povyshenie-kvalifikacii22/programmy-povysheniya-kvalifikacii-v-o/, свободный. - Загл. с экрана \A \
        9. Строительство [Электронный ресурс] // niisf.org - Режим доступа: https://niisf.org/obuchenie/stroitelstvo, свободный. - Загл. с экрана \A \
        10. Повышение квалификации по строительству | Новосибирск [Электронный ресурс] // novosibirsk.igosstroy.ru - Режим доступа: https://novosibirsk.igosstroy.ru/programmy-obucheniya-po-stroitelstvu/, свободный. - Загл. с экрана \A \
        11. Курс Проектная документация и архитектурно-строительная... [Электронный ресурс] // vgaps.ru - Режим доступа: https://vgaps.ru/seminar/pk-proektnaya-dokumentaciya-i-arkhitekturno-stroitelnaya-grafika, свободный. - Загл. с экрана \A \
        12. «Архитектурно-строительное проектирование и подготовка...» [Электронный ресурс] // maspk.ru - Режим доступа: https://maspk.ru/corporate/povyshenie-kvalifikatsii/proektirovanie1/arkhitekturno-stroitelnoe-proektirovanie-i-podgotovka-proektnoy-dokumentatsii/, свободный. - Загл. с экрана \A \
        13. Презентация PowerPoint [Электронный ресурс] // arpo.su - Режим доступа: https://arpo.su/wp-content/uploads/2024/03/plan-arpo-2024.pdf, свободный. - Загл. с экрана';

          @include media-breakpoint-down(md) {
            content: '1. Проблема квалифицированных кадров в строительстве | Статья... [Электронный ресурс] // moluch.ru - Режим доступа: https://moluch.ru/archive/79/13889/, свободный. \A \
          2. Эксперты обсудили проблему квалификационных компетенций... [Электронный ресурс] // asonsk.ru - Режим доступа: https://asonsk.ru/information/eksperty-obsudili-problemu-otstavaniya-i-kvalifikatsionnykh-kompetentsiy-v-sfere-stroitelstva/, свободный. \A \
          3. Дородных А. А. Проблемы подготовки специалистов в области строительства // Интеллектуальный потенциал XXI века: ступени познания. 2010. №2. URL: https://cyberleninka.ru/article/n/problemy-podgotovki-spetsialistov-v-oblasti-stroitelstva (дата обращения: 26.11.2024). \A \
          4. Report [Электронный ресурс] // nostroy-sdo.ru - Режим доступа: https://nostroy-sdo.ru/staticcontent/презентация нок нострой.pdf, свободный.';
          }
        }
      }
    }

    .blurred-content__card {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translateX(-50%) translateY(-50%);

      background: $background-white;
      width: 360px;
      border-radius: 20px;
      box-shadow: 0 4px 40px 0 rgba(0, 0, 0, 0.05);
      z-index: 1;

      @include media-breakpoint-down(md) {
        width: calc(100vw - 40px);
      }

      .blurred-content__card__content {
        position: relative;
        display: flex;
        flex-direction: column;
        gap: 24px;
        padding: 40px 24px;
        font-family: 'Manrope', sans-serif;

        @include media-breakpoint-down(md) {
          padding: 24px;
        }

        .blurred-content__card__title {
          line-height: 26px;
          font-weight: 600;
          font-size: 18px;
          width: 70%;
        }

        .blurred-content__card__text {
          line-height: 20px;
          font-size: 14px;
          font-weight: 500;
          text-wrap: balance;
        }

        .blurred-content__card__lock {
          position: absolute;
          right: 24px;
          top: 0;
          background: #ebebf0;
          border-radius: 0px 0px 16px 16px;
          padding: 40px 16px 16px 16px;
          display: flex;
          align-items: center;
        }
      }
    }
  }
}
</style>
